// Shared presentation helpers for the planned monthly-audit workflow (Audits
// guide). One audit per program per calendar month; it moves through Not
// Started → In Progress → Corrective Actions Open → Complete. The status shown
// on the history list is derived from the stored state + start stamp; the true
// "Corrective Actions Open" distinction (open vs. verified actions) is resolved
// on the detail page, where the corrective-action list is loaded.
import { jsx as _jsx } from "react/jsx-runtime";
import { SpecraBadge } from '@mono/ui-components';
import { formatLocalYmd } from '../_shared/api/nullable';
// The current period key in UTC — matches the server's
// time.Now().UTC().Format("2006-01") so the "this month" lookup agrees on the
// last/first day of a month across timezones.
export function currentPeriodMonth() {
    const now = new Date();
    const y = now.getUTCFullYear();
    const m = String(now.getUTCMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
}
// "2026-07" → "Jul 2026".
export function monthLabel(periodMonth) {
    const [y, m] = periodMonth.split('-').map(Number);
    if (!y || !m) return periodMonth;
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC'
    });
}
// The audit's period for display — the monthly key when present, else the
// legacy start/end range (pre-overhaul rows carry no period_month).
export function periodLabel(a) {
    if (a.periodMonth) return monthLabel(a.periodMonth);
    const start = formatLocalYmd(a.periodStart);
    const end = formatLocalYmd(a.periodEnd);
    if (start || end) return `${start ?? '…'} – ${end ?? '…'}`;
    return '—';
}
export function auditorLabel(a) {
    return a.assignedTo || a.startedBy || a.completedBy || '—';
}
// A percentage in every state so the Score column reads consistently. Derive the
// FINAL compliance figure from the counts rather than the frozenScore string:
// passCount/totalQuestions is final compliance in every frozen state (live for an
// open one), so it's uniform where the string isn't. SAD-143: a completed
// self-assessment's frozenScore is the FIRST-PASS quality percentage — append it
// as a labelled secondary so the panel reads "100% · 54% first-pass", never a
// bare first-pass figure that looks like a compliance failure. Fall back to the
// raw frozenScore only when there's nothing to divide (an all-N/A audit freezes
// "—" with a zero total).
export function auditScoreLabel(a) {
    if (a.totalQuestions > 0) {
        const final = `${Math.floor(a.passCount * 100 / a.totalQuestions)}%`;
        if (a.state === 'Complete' && a.frozenScore) return `${final} · ${a.frozenScore} first-pass`;
        return final;
    }
    if (a.frozenScore) return a.frozenScore;
    return '—';
}
export function deriveAuditStatus(a) {
    const s = a.state ?? '';
    if ([
        'Complete',
        'Published',
        'Closed Complete'
    ].includes(s)) {
        return {
            label: 'Complete',
            variant: 'success'
        };
    }
    if (s === 'Corrective Actions Open') {
        return {
            label: 'Correction Needed',
            variant: 'warning'
        };
    }
    if (a.startedAt || [
        'In Progress',
        'In Review'
    ].includes(s)) {
        return {
            label: 'In Progress',
            variant: 'info'
        };
    }
    return {
        label: 'Not Started',
        variant: 'neutral'
    };
}
export function AuditStatusBadge({ audit }) {
    const { label, variant } = deriveAuditStatus(audit);
    return /*#__PURE__*/ _jsx(SpecraBadge, {
        variant: variant,
        children: label
    });
}
